import React from 'react';
import {find as _find} from 'lodash';
import {ButtonSwitch, ButtonSwitchOption,} from '@gisatcz/ptr-atoms';
import PropTypes from 'prop-types';

import vectorLayers from '../../data/layers/vectorLayers';
import configuration from '../../data/configuration';
import {Intervals, IntervalsLine, SectionHeader, Symbol, ValuesByColor,} from '../Legend';
import styles from '../../data/styles';

import './style.scss';
import ReactGA from 'react-ga';

class LayerControl extends React.PureComponent {
	static propTypes = {
		activeMap: PropTypes.object,
		layerKey: PropTypes.string,
	};

	handleVectorLayer(layerKey, styleKey) {
		ReactGA.event({
			category: 'Map content',
			action: 'Change vector layer style',
			label: 'map',
		});

		const props = this.props;

		const mapKey = props.activeMap.key;
		const smallMapKey = props.pairConfig.maps[mapKey];
		const smallLayerKey = props.pairConfig.layers[layerKey];
		const smallStyleKey = props.pairConfig.styles[styleKey];

		const layerData = _find(vectorLayers, layer => layer.key === layerKey);
		const smallLayerData = _find(
			vectorLayers,
			layer => layer.key === smallLayerKey
		);

		const currentLayers = props.activeMap?.data?.layers || [];
		const existingLayer = _find(
			currentLayers,
			layer => layer.key === layerKey
		);

		if (styleKey) {
			if (!existingLayer) {
				const layerOrder = configuration.layerOrder[mapKey];
				const index = this.getLayerIndex(
					layerKey,
					layerOrder,
					currentLayers.map(layer => layer.key)
				);

				this.props.addVectorLayer(mapKey, layerData, index);
				this.props.addVectorLayer(smallMapKey, smallLayerData, index);
			}

			const styleDefinition = this.props.styles[styleKey]?.data.definition;
			const smallStyleDefinition = this.props.styles[smallStyleKey]?.data
				.definition;

			// set correct style definition to renderAs option
			if (
				configuration.renderAsStylePairs &&
				existingLayer?.options?.renderAs
			) {
				const canvasStyleKey = configuration.renderAsStylePairs[styleKey];
				const canvasStyleDefinition = this.props.styles[canvasStyleKey];
				const renderAs = existingLayer.options.renderAs.map(item => {
					if (item.options?.renderingTechnique === 'canvas') {
						return {
							...item,
							options: {
								...item.options,
								style: canvasStyleDefinition.data.definition,
							},
						};
					} else {
						return item;
					}
				});

				this.props.setLayerRenderAs(mapKey, layerKey, renderAs);
			}

			this.props.setLayerStyle(mapKey, layerKey, styleDefinition);
			this.props.setLayerStyle(
				smallMapKey,
				smallLayerKey,
				smallStyleDefinition
			);
		} else {
			this.props.removeVectorLayer(mapKey, layerKey);
			this.props.removeVectorLayer(smallMapKey, smallLayerKey);
		}

		this.props.changeActiveLayerStyle(layerKey, styleKey);
	}

	getLayerIndex(key, order, currentList) {
		const keyIndexInOrder = order.indexOf(key);

		let i = 0;
		for (i; i < currentList.length; i++) {
			let item = currentList[i];
			let itemIndexInOrder = order.indexOf(item);

			if (itemIndexInOrder > keyIndexInOrder) {
				break;
			}
		}

		return i;
	}

	render() {
		return (
			<div className="eo4sdDrrInsarVietnam20-layer-control-wrapper">
				{this.renderVectorLayerSwitch(this.props.layerKey)}
				{this.renderVectorLayerLegend(this.props.layerKey)}
			</div>
		);
	}

	renderVectorLayerSwitch(layerKey) {
		const props = this.props;
		const availableStyleKeys = props.layerStylesConfig?.[layerKey]?.available;
		const availableStyles = availableStyleKeys?.map(key => props.styles[key]);
		const activeStyleKey = props.layerStylesConfig?.[layerKey]?.active;

		const content = availableStyles.map(style => (
			<ButtonSwitchOption
				key={style.key}
				value={style.key}
				active={style.key === activeStyleKey}
				small={this.props.small}
			>
				{style.data?.nameDisplay}
			</ButtonSwitchOption>
		));

		if (!props?.hideRemoval) {
			content.push(
				<ButtonSwitchOption key="none" value={null} active={!activeStyleKey}>
					None
				</ButtonSwitchOption>
			);
		}

		return (
			<ButtonSwitch onClick={this.handleVectorLayer.bind(this, layerKey)} ghost>
				{content}
			</ButtonSwitch>
		);
	}

	renderVectorLayerLegend(layerKey) {
		const activeStyleKey = this.props.layerStylesConfig?.[layerKey]?.active;

		if (activeStyleKey) {
			let content = null;

			if (activeStyleKey === 'riskAssessmentZonesDeformationBig') {
				const classes = styles.p75AttributeIntervals.attributeClasses;
				const noDataColor = styles.zoneBase.fill;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader
							title="Annual displacement rate [mm/year]"
							subtitle="From zonal 75-percentile"
						/>
						<IntervalsLine
							styleProperty="fill"
							classes={classes}
							noData={noDataColor}
							longerTails
						/>
					</div>
				);
			} else if (activeStyleKey === 'riskAssessmentZonesLandUseBig') {
				const items = styles.landUseClassification.attributeValues;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader title="Land use classes" />
						<ValuesByColor data={items} />
					</div>
				);
			} else if (activeStyleKey === 'riskAssessmentZonesChangeYearBig') {
				const items = styles.chngYearAttributeValues.attributeValues;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader
							title="Year of built-up change"
							subtitle="based on WSF Evolution &copy; DLR"
						/>
						<ValuesByColor data={items} />
					</div>
				);
			} else if (activeStyleKey === 'riskAssessmentZonesChangeTypeBig') {
				let items = {
					...styles.chngTypeAttributeValues.attributeValues,
					...styles.chngTypeLandUseAttributeValues.attributeValues,
				};
				const noDataColor = styles.zoneBase.fill;

				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader
							title="Built-up change type"
							subtitle="in the last decade, based on interpretation of Google Earth imagery & WSF Evolution &copy; DLR"
						/>
						<ValuesByColor data={items} noData={noDataColor} />
					</div>
				);
			}

			// Roads
			else if (activeStyleKey === 'riskAssessmentRoadsVelAvgBig') {
				const classes = styles.velAvgRoads.attributeClasses;
				const noDataColor = styles.roadBase.outlineColor;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader
							title="Annual displacement rate [mm/year]"
							subtitle="From nearby points (20m buffer)"
						/>
						<IntervalsLine
							styleProperty="outlineColor"
							classes={classes}
							noData={noDataColor}
							longerTails
						/>
					</div>
				);
			} else if (activeStyleKey === 'riskAssessmentRoadsTypologyBig') {
				const classes = styles.roadTypology.attributeClasses;
				const baseColor = styles.roadBase.outlineColor;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader title="Type" subtitle="based on Open Street Map" />
						<Intervals
							styleProperty="outlineWidth"
							classes={classes}
							baseColor={baseColor}
						/>
					</div>
				);
			}

			// Points
			else if (activeStyleKey === 'interferometryPointsVelAvgBig') {
				const classes = styles.velAvg.attributeClasses;
				const noDataColor = styles.interferometryBase.fill;
				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader title="Annual displacement rate [mm/year]" />
						<IntervalsLine
							styleProperty="fill"
							classes={classes}
							noData={noDataColor}
							longerTails
						/>
					</div>
				);
			} else if (
				activeStyleKey === 'interferometryPointsSpatialPatternTypeBig'
			) {
				let items = styles.spatialPatternType.attributeValues;
				const noDataColor = styles.zoneBase.fill;

				content = (
					<div className="eo4sdDrrInsarVietnam20-legend-section">
						<SectionHeader title="Type of spatial pattern: cluster / outlier" />
						<ValuesByColor
							data={items}
							noData={noDataColor}
							noDataTitle="Not significant clustering"
						/>
					</div>
				);
			} else if (activeStyleKey === 'interferometryPointsVelAvgAlarmBig') {
				const classes = styles.velAvg.attributeClasses;
				const noDataColor = styles.interferometryBase.fill;

				const alarmStyle = {
					...styles.interferometryBase,
					...styles.alarms.attributeValues[10],
				};

				content = (
					<>
						<div className="eo4sdDrrInsarVietnam20-legend-section">
							<SectionHeader title="Annual displacement rate [mm/year]" />
							<IntervalsLine
								styleProperty="fill"
								classes={classes}
								noData={noDataColor}
								longerTails
							/>
						</div>
						<div className="eo4sdDrrInsarVietnam20-legend-section">
							<SectionHeader title="Alarm (for the last year)" />
							<Symbol style={alarmStyle} />
						</div>
					</>
				);
			}

			return (
				<div className="eo4sdDrrInsarVietnam20-vector-layer-control-legend">
					{content}
				</div>
			);
		} else {
			return null;
		}
	}
}

export default LayerControl;
